import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { hasAnyRole } from '../utils/roles';

const monthNames = [
  'Ocak',
  'Şubat',
  'Mart',
  'Nisan',
  'Mayıs',
  'Haziran',
  'Temmuz',
  'Ağustos',
  'Eylül',
  'Ekim',
  'Kasım',
  'Aralık'
];

const dayNames = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

const typeLabels = {
  toplanti: 'Toplantı',
  egitim: 'Eğitim',
  sosyal: 'Sosyal',
  tatil: 'Özel Gün',
  proje: 'Proje',
  genel: 'Genel'
};

const emptyEventForm = {
  title: '',
  description: '',
  type: 'genel',
  date: '',
  startTime: '09:00',
  endTime: '10:00',
  location: '',
  isOnline: false,
  meetingLink: ''
};

const EventCalendarPage = () => {
  const [profile, setProfile] = useState(null);
  const [events, setEvents] = useState([]);
  const [highlights, setHighlights] = useState({ upcomingEvents: [], birthdays: [] });
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [eventForm, setEventForm] = useState(emptyEventForm);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const canCreateEvent = profile && hasAnyRole(profile.role, ['super_admin', 'company_admin']);

  const fetchCalendarData = async () => {
    try {
      setLoading(true);
      setError('');

      const [profileResponse, eventsResponse, highlightsResponse] = await Promise.all([
        api.getProfile(),
        api.getEvents(),
        api.getEventHighlights()
      ]);

      setProfile(profileResponse.user);
      setEvents(eventsResponse.events || []);
      setHighlights(highlightsResponse.highlights || { upcomingEvents: [], birthdays: [] });
    } catch (fetchError) {
      console.error('Event calendar error:', fetchError);
      setError('Takvim verileri yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCalendarData();
  }, []);

  const daysInMonth = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const totalDays = lastDay.getDate();
    const startingDayOfWeek = (firstDay.getDay() + 6) % 7;
    const days = [];

    for (let index = 0; index < startingDayOfWeek; index += 1) {
      days.push(null);
    }

    for (let day = 1; day <= totalDays; day += 1) {
      days.push(day);
    }

    return days;
  }, [currentDate]);

  const getEventsForDate = (day) => {
    if (!day) {
      return [];
    }

    const dateKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter((event) => event.startDate.slice(0, 10) === dateKey);
  };

  const handleAttendance = async (eventId, status) => {
    try {
      await api.respondToEvent(eventId, { status });
      await fetchCalendarData();
    } catch (attendanceError) {
      console.error('Respond to event error:', attendanceError);
      setError('Etkinlik katılım bilgisi güncellenemedi.');
    }
  };

  const handleCreateEvent = async (event) => {
    event.preventDefault();

    try {
      setSubmitting(true);
      await api.createEvent({
        title: eventForm.title,
        description: eventForm.description,
        type: eventForm.type,
        startDate: eventForm.date,
        endDate: eventForm.date,
        startTime: eventForm.startTime,
        endTime: eventForm.endTime,
        location: eventForm.location,
        isOnline: eventForm.isOnline,
        meetingLink: eventForm.meetingLink
      });

      setEventForm(emptyEventForm);
      setShowCreateForm(false);
      await fetchCalendarData();
    } catch (createError) {
      console.error('Create event error:', createError);
      setError('Etkinlik oluşturulamadı.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '360px' }}>
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row mb-4">
        <div className="col-12">
          <div className="box">
            <div className="box-body d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
              <div>
                <h3 className="mb-2">Etkinlik ve Özel Gün Takvimi</h3>
                <p className="text-muted mb-0">
                  Kurumsal etkinlikleri, yakın doğum günlerini ve katılım durumlarını tek ekranda
                  yönetin.
                </p>
              </div>
              {canCreateEvent && (
                <button className="btn btn-primary" onClick={() => setShowCreateForm(true)}>
                  Etkinlik Ekle
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {showCreateForm && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="box">
              <div className="box-header d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0">Yeni Etkinlik</h5>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowCreateForm(false)}>
                  Kapat
                </button>
              </div>
              <div className="box-body">
                <form onSubmit={handleCreateEvent}>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Başlık</label>
                      <input
                        className="form-control"
                        value={eventForm.title}
                        onChange={(event) =>
                          setEventForm((current) => ({ ...current, title: event.target.value }))
                        }
                        required
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Tür</label>
                      <select
                        className="form-select"
                        value={eventForm.type}
                        onChange={(event) =>
                          setEventForm((current) => ({ ...current, type: event.target.value }))
                        }
                      >
                        {Object.entries(typeLabels).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Tarih</label>
                      <input
                        className="form-control"
                        type="date"
                        value={eventForm.date}
                        onChange={(event) =>
                          setEventForm((current) => ({ ...current, date: event.target.value }))
                        }
                        required
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Başlangıç</label>
                      <input
                        className="form-control"
                        type="time"
                        value={eventForm.startTime}
                        onChange={(event) =>
                          setEventForm((current) => ({ ...current, startTime: event.target.value }))
                        }
                        required
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Bitiş</label>
                      <input
                        className="form-control"
                        type="time"
                        value={eventForm.endTime}
                        onChange={(event) =>
                          setEventForm((current) => ({ ...current, endTime: event.target.value }))
                        }
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Konum</label>
                      <input
                        className="form-control"
                        value={eventForm.location}
                        onChange={(event) =>
                          setEventForm((current) => ({ ...current, location: event.target.value }))
                        }
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Açıklama</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={eventForm.description}
                        onChange={(event) =>
                          setEventForm((current) => ({
                            ...current,
                            description: event.target.value
                          }))
                        }
                        required
                      />
                    </div>
                  </div>
                  <div className="d-flex justify-content-end gap-2 mt-4">
                    <button type="button" className="btn btn-outline-secondary" onClick={() => setShowCreateForm(false)}>
                      Vazgeç
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={submitting}>
                      {submitting ? 'Kaydediliyor...' : 'Etkinliği Yayınla'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="row">
        <div className="col-lg-8 mb-4">
          <div className="box">
            <div className="box-header d-flex justify-content-between align-items-center">
              <h4 className="card-title mb-0">Aylık Takvim</h4>
              <div className="d-flex align-items-center gap-2">
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={() =>
                    setCurrentDate(
                      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
                    )
                  }
                >
                  Geri
                </button>
                <div className="fw-semibold">
                  {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                </div>
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={() =>
                    setCurrentDate(
                      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
                    )
                  }
                >
                  İleri
                </button>
              </div>
            </div>
            <div className="box-body">
              <div className="row text-center fw-semibold mb-2">
                {dayNames.map((dayName) => (
                  <div key={dayName} className="col">
                    {dayName}
                  </div>
                ))}
              </div>

              {Array.from({ length: Math.ceil(daysInMonth.length / 7) }, (_, weekIndex) => (
                <div key={weekIndex} className="row mb-2">
                  {daysInMonth.slice(weekIndex * 7, (weekIndex + 1) * 7).map((day, dayIndex) => (
                    <div key={`${weekIndex}-${dayIndex}`} className="col">
                      <div className="border rounded p-2 h-100" style={{ minHeight: '110px' }}>
                        {day && (
                          <>
                            <div className="fw-semibold mb-2">{day}</div>
                            <div className="d-grid gap-1">
                              {getEventsForDate(day).slice(0, 3).map((event) => (
                                <button
                                  key={event.id}
                                  className="btn btn-light btn-sm text-start border"
                                  onClick={() => setSelectedEvent(event)}
                                >
                                  <div className="small fw-semibold">{event.startTime}</div>
                                  <div className="small text-truncate">{event.title}</div>
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="col-lg-4">
          <div className="box mb-4">
            <div className="box-header">
              <h5 className="card-title mb-0">Yaklaşan Etkinlikler</h5>
            </div>
            <div className="box-body d-grid gap-3">
              {highlights.upcomingEvents.map((event) => (
                <button
                  key={event.id}
                  className="btn btn-light border text-start"
                  onClick={() => setSelectedEvent(event)}
                >
                  <div className="fw-semibold">{event.title}</div>
                  <div className="small text-muted">
                    {new Date(event.startDate).toLocaleDateString('tr-TR')} - {event.startTime}
                  </div>
                  <div className="small text-muted">
                    {typeLabels[event.type] || event.type} / {event.location || 'Konum bekleniyor'}
                  </div>
                </button>
              ))}

              {highlights.upcomingEvents.length === 0 && (
                <div className="text-muted small">Yaklaşan etkinlik bulunmuyor.</div>
              )}
            </div>
          </div>

          <div className="box">
            <div className="box-header">
              <h5 className="card-title mb-0">Doğum Günü ve Özel Günler</h5>
            </div>
            <div className="box-body d-grid gap-3">
              {highlights.birthdays.map((person) => (
                <div key={person.id} className="border rounded p-3">
                  <div className="fw-semibold">
                    {person.firstName} {person.lastName}
                  </div>
                  <div className="small text-muted">{person.department}</div>
                  <div className="small text-muted">
                    {new Date(person.birthDate).toLocaleDateString('tr-TR', {
                      day: 'numeric',
                      month: 'long'
                    })}
                  </div>
                </div>
              ))}

              {highlights.birthdays.length === 0 && (
                <div className="text-muted small">Yaklaşan doğum günü kaydı bulunmuyor.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {selectedEvent && (
        <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0, 0, 0, 0.45)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{selectedEvent.title}</h5>
                <button type="button" className="btn-close" onClick={() => setSelectedEvent(null)} />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="small text-muted">Tür</div>
                    <div>{typeLabels[selectedEvent.type] || selectedEvent.type}</div>
                  </div>
                  <div className="col-md-6">
                    <div className="small text-muted">Tarih</div>
                    <div>{new Date(selectedEvent.startDate).toLocaleDateString('tr-TR')}</div>
                  </div>
                  <div className="col-md-6">
                    <div className="small text-muted">Saat</div>
                    <div>
                      {selectedEvent.startTime} - {selectedEvent.endTime}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="small text-muted">Konum</div>
                    <div>{selectedEvent.location || 'Belirtilmedi'}</div>
                  </div>
                  <div className="col-12">
                    <div className="small text-muted">Açıklama</div>
                    <div>{selectedEvent.description}</div>
                  </div>
                  <div className="col-12">
                    <div className="small text-muted">Katılım</div>
                    <div>
                      {selectedEvent.acceptedCount} kabul / {selectedEvent.attendeeCount} toplam
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer d-flex justify-content-between">
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-success btn-sm"
                    onClick={() => handleAttendance(selectedEvent.id, 'accepted')}
                  >
                    Katılacağım
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => handleAttendance(selectedEvent.id, 'tentative')}
                  >
                    Belki
                  </button>
                  <button
                    className="btn btn-outline-danger btn-sm"
                    onClick={() => handleAttendance(selectedEvent.id, 'declined')}
                  >
                    Katılamayacağım
                  </button>
                </div>
                <button className="btn btn-link" onClick={() => setSelectedEvent(null)}>
                  Kapat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventCalendarPage;
